/** Stable machine-readable error codes. These are part of the API contract:
 *  agents and the browser both branch on them, so do not rename in place. */
export const ERROR_CODES = [
    // --- request shape ---
    'INVALID_REQUEST',
    'NOT_FOUND',
    'UNAUTHENTICATED',
    /** The identity provider (Supabase JWKS) could not be reached. Retryable. */
    'AUTH_PROVIDER_UNAVAILABLE',
    'FORBIDDEN',
    /** An unexpected fault on our side. Never used for anything the caller did
     *  wrong — misreporting our own bug as a 400 sends an agent into a retry
     *  loop fixing a request that was already correct. */
    'INTERNAL',
    // --- limits ---
    /** A per-account fair-use cap, e.g. the workspace cap. Not a pilot row limit. */
    'QUOTA_EXCEEDED',
    'LIMIT_EXCEEDED',
    // --- dataset / snapshot ---
    'DATASET_SCHEMA_INVALID',
    'ROW_TYPE_MISMATCH',
    'ROW_VALUE_NOT_FINITE',
    'ROW_REQUIRED_VALUE_MISSING',
    'ROW_UNKNOWN_COLUMN',
    'ROW_MISSING_COLUMN',
    'DUPLICATE_UNIQUE_KEY',
    'SNAPSHOT_INCOMPATIBLE',
    // --- report definition ---
    'REPORT_DEFINITION_INVALID',
    'UNKNOWN_COLUMN_REFERENCE',
    'UNKNOWN_WIDGET_REFERENCE',
    'DUPLICATE_WIDGET_ID',
    'UNSUPPORTED_AGGREGATION_FOR_TYPE',
    'LAYOUT_INVALID',
    // --- concurrency ---
    'REVISION_CONFLICT',
    'SNAPSHOT_CONFLICT',
    'SOURCE_NEEDS_RECONNECT',
    // --- history ---
    /** A restore's target definition deep-equals the current one: nothing to
     *  write. Not a conflict — the caller's revision was correct. */
    'NO_CHANGE',
    // --- export ---
    'EXPORT_FAILED',
    'EXPORT_BUSY',
    'EXPORT_TIMEOUT',
    // --- rate limits ---
    'RATE_LIMITED',
    // --- publication ---
    /** Publishing a report that has no active snapshot. */
    'NOT_READY',
    /** Updating the published version of a report that has no link. */
    'NOT_PUBLISHED',
    /** A plan's row cap. Separate from LIMIT_EXCEEDED, a ceiling no plan lifts. */
    'PLAN_LIMIT_EXCEEDED',
    // --- fetch ---
    'FETCH_FAILED'
];
export const FETCH_FAILED_REASONS = [
    'invalid_url',
    'unsupported_scheme',
    'blocked_port',
    'blocked_address',
    'dns',
    'connect',
    'connect_timeout',
    'timeout',
    'tls',
    'bad_redirect',
    'too_many_redirects',
    'http_status',
    'not_a_file',
    'source_unavailable',
    'provider_unavailable'
];
/** Reasons a retry may fix. http_status is transient only for 5xx. */
export const TRANSIENT_FETCH_REASONS = ['dns', 'connect', 'connect_timeout', 'timeout'];
/** The single error type crossing every boundary (HTTP, MCP, in-process). */
export class ReportPadError extends Error {
    code;
    issues;
    /** Extra structured payload, e.g. `{ currentRevision }` on a conflict. */
    details;
    constructor(code, message, options = {}) {
        super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
        this.name = 'ReportPadError';
        this.code = code;
        this.issues = options.issues ?? [{ code, message }];
        this.details = options.details ?? {};
    }
    /** HTTP status for this error class. */
    get httpStatus() {
        switch (this.code) {
            case 'UNAUTHENTICATED':
                return 401;
            case 'QUOTA_EXCEEDED':
                return 403;
            case 'FORBIDDEN':
                return 403;
            case 'PLAN_LIMIT_EXCEEDED':
                return 403;
            case 'NOT_FOUND':
                return 404;
            case 'REVISION_CONFLICT':
            case 'SNAPSHOT_CONFLICT':
            case 'SOURCE_NEEDS_RECONNECT':
            case 'NOT_READY':
            case 'NOT_PUBLISHED':
                return 409;
            case 'LIMIT_EXCEEDED':
                return 413;
            case 'EXPORT_BUSY':
            case 'RATE_LIMITED':
                return 429;
            case 'FETCH_FAILED':
                return 502;
            case 'AUTH_PROVIDER_UNAVAILABLE':
                return 503;
            case 'EXPORT_TIMEOUT':
                return 504;
            case 'EXPORT_FAILED':
            case 'INTERNAL':
                return 500;
            default:
                return 400;
        }
    }
    toJSON() {
        return { error: { code: this.code, message: this.message, issues: this.issues, details: this.details } };
    }
    static isReportPadError(value) {
        return value instanceof ReportPadError;
    }
}
/** Collects issues and throws once, so a caller sees every problem at once
 *  rather than fixing them one round-trip at a time. */
export class IssueCollector {
    issues = [];
    add(code, message, location) {
        this.issues.push(location ? { code, message, location } : { code, message });
    }
    get ok() {
        return this.issues.length === 0;
    }
    /** Throws a ReportPadError carrying every collected issue. No-op if clean. */
    throwIfFailed(code, summary, details) {
        if (this.ok)
            return;
        const first = this.issues[0];
        const message = this.issues.length === 1 ? `${summary}: ${first.message}` : `${summary}: ${first.message} (+${this.issues.length - 1} more)`;
        throw new ReportPadError(code, message, { issues: this.issues, details: details ?? {} });
    }
}
export function limitExceeded(what, actual, max) {
    return new ReportPadError('LIMIT_EXCEEDED', `${what}: ${actual} exceeds the pilot limit of ${max}.`, {
        details: { actual, max }
    });
}
const PLAN_LIMIT_MESSAGES = {
    rows: (limit) => `This workspace holds up to ${limit} rows per dataset.`,
    published_links: (limit) => `This workspace's owner has reached ${limit} published links. Unpublish one, then try again.`,
    daily_writes: (limit) => `This workspace's owner has reached ${limit} data updates and publishes in 24 hours. Try again later.`
};
/** Plan-neutral on purpose: agents read this message, and ChatGPT apps may not
 *  show plans or upgrade links. The web adds the upgrade line from `details`. */
export function planLimitExceeded(limit, plan, kind = 'rows') {
    return new ReportPadError('PLAN_LIMIT_EXCEEDED', PLAN_LIMIT_MESSAGES[kind](limit.toLocaleString('en-US')), {
        details: { kind, limit, plan }
    });
}
//# sourceMappingURL=errors.js.map